import { ArrowUpRight, CalendarDays, Clock3, MapPin, MessageCircleQuestion, ShieldCheck, WalletCards } from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'
import type { Brief, Company } from '../../types/domain'
import { currency, date } from '../../lib/format'
import { useAppStore } from '../../store/AppStore'
import { Badge } from '../ui/Badge'
import { Button } from '../ui/Button'

export function BriefOpportunityCard({ brief, company, compact = false, publicMode = false }: { brief: Brief; company?: Company; compact?: boolean; publicMode?: boolean }) {
  const { state, applyToBrief, askAboutBrief } = useAppStore()
  const navigate = useNavigate()
  const athleteId = state.currentUser?.athleteId
  const hasApplied = Boolean(athleteId && brief.applicants.includes(athleteId))
  const status = athleteId ? state.connections.find((item) => item.athleteId === athleteId && item.briefId === brief.id)?.status : undefined
  const deadline = new Date(brief.startDate)
  deadline.setDate(deadline.getDate() - 14)

  const openOpportunity = () => {
    if (publicMode || state.currentUser?.role !== 'athlete') navigate(`/opportunites#${brief.id}`)
    else navigate(`/app/athlete/opportunites?brief=${brief.id}`)
  }

  return <article className={`opportunity-card opportunity-card--next ${compact ? 'is-compact' : ''}`} id={brief.id}>
    <div className="opportunity-card__glow" aria-hidden="true" />
    <header>
      <Link className="company-avatar company-avatar--modern company-avatar--image" to={company ? `/entreprises/${company.slug}` : '/entreprises'} aria-label={company ? `Voir ${company.name}` : 'Voir les entreprises'}>{company?.logoImage ? <img src={company.logoImage} alt="" /> : company?.logo ?? 'PME'}</Link>
      <div className="opportunity-card__company"><Link to={company ? `/entreprises/${company.slug}` : '/entreprises'}>{company?.name ?? 'Entreprise'}</Link><small>{company?.sector} · {company?.city}</small></div>
      {company?.verified && <Badge tone="green"><ShieldCheck size={13} />Vérifiée</Badge>}
    </header>
    <div className="opportunity-card__title"><span className="opportunity-status"><i />Ouverte</span><h3>{brief.title}</h3><p>{brief.objective}</p></div>
    <div className="opportunity-card__stats">
      <div><WalletCards /><span>Budget<strong>{currency(brief.budget)}</strong></span></div>
      <div><Clock3 /><span>Durée<strong>{brief.durationMonths} mois</strong></span></div>
      <div><MapPin /><span>Zone<strong>{brief.territory}</strong></span></div>
    </div>
    <div className="opportunity-card__match"><span>Profils recherchés</span><div className="tag-row">{brief.requiredCriteria.slice(0, 2).map((item) => <span className="tag" key={item}>{item}</span>)}</div></div>
    <footer>
      <span className="opportunity-deadline"><CalendarDays size={15} />Candidater avant le {date(deadline.toISOString())}</span>
      <div className="opportunity-actions">
        {publicMode || !athleteId ? <Button onClick={openOpportunity}>Voir l’opportunité <ArrowUpRight size={16} /></Button> : <>
          <Button variant="ghost" onClick={() => { const id = askAboutBrief(brief.id, athleteId); navigate(`/app/messages?conversation=${id}`) }}><MessageCircleQuestion size={16} />Poser une question</Button>
          <Button variant="action" disabled={hasApplied} onClick={() => { const id = applyToBrief(brief.id, athleteId); navigate(`/app/messages?conversation=${id}`) }}>{hasApplied ? 'Intérêt envoyé' : 'Je suis intéressé'}</Button>
        </>}
      </div>
      {status && <span className="connection-status">{status === 'interest_sent' ? 'Intérêt envoyé' : status === 'conversation_started' ? 'Échange commencé' : status === 'proposal_in_progress' ? 'Proposition en cours' : 'Partenariat créé'}</span>}
    </footer>
  </article>
}
